// Copyright 2025, University of Maryland, All Rights Reserved
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Trash2, ChevronUp, ChevronDown } from "lucide-react"
import React from "react"
import type { SchemaField } from "@/types/schema-fields"

export interface BaseFieldEditorProps {
  field: SchemaField
  onFieldUpdate: (field: SchemaField, updates: Partial<SchemaField>) => void
  onRemove: (field: SchemaField) => void
  onMoveUp?: (field: SchemaField) => void
  onMoveDown?: (field: SchemaField) => void
  isLoading?: boolean
  readonly_id?: boolean // Make field ID readonly
  non_deletable?: boolean // Make field non-deletable
  hide_title?: boolean // Hide the title field
  children?: React.ReactNode // Field-specific configuration
  // Position info for arrow buttons
  isFirst?: boolean
  isLast?: boolean
}

export const BaseField = ({
  field, 
  onFieldUpdate, 
  onRemove, 
  onMoveUp,
  onMoveDown,
  isLoading,
  readonly_id = false,
  non_deletable = false,
  hide_title = false,
  children,
  isFirst = false,
  isLast = false
}: BaseFieldEditorProps) => {
  const handleFieldUpdate = (updates: Partial<SchemaField>) => {
    onFieldUpdate(field, updates)
  }

  return (
    <div className="p-3 bg-gray-50 rounded-lg space-y-3">
      {/* Header Row: Field Type Badge + Reorder Buttons + Delete Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-xs">
            {field.config.title}
          </Badge>
        </div>
        <div className="flex items-center gap-1">
          {(onMoveUp || onMoveDown) && (
            <div className="flex items-center">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onMoveUp?.(field)}
                disabled={isLoading || isFirst}
                title="Move field up"
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onMoveDown?.(field)}
                disabled={isLoading || isLast}
                title="Move field down"
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
            </div>
          )}
          {!non_deletable && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onRemove(field)}
              disabled={isLoading}
              title="Remove Field"
            >
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          )}
        </div>
      </div>

      {/* Common Fields: ID, Title, Description */}
      <div className={`grid grid-cols-1 gap-3 ${
        readonly_id && hide_title ? 'md:grid-cols-1' : 
        readonly_id || hide_title ? 'md:grid-cols-2' : 
        'md:grid-cols-3'
      }`}>
        {!readonly_id && (
          <div className="space-y-1">
            <Label className="text-xs">Field ID *</Label>
            <Input
              value={field.id}
              onChange={(e) => handleFieldUpdate({ id: e.target.value })}
              placeholder="field_id"
              className="font-mono text-sm h-8"
              disabled={isLoading || readonly_id}
            />
            <p className="text-xs text-muted-foreground">
              Property name in JSON schema
            </p>
          </div>
        )}
        
        {!hide_title && (
          <div className="space-y-1">
            <Label className="text-xs">Title</Label>
            <Input
              value={field.title || ''}
              onChange={(e) => handleFieldUpdate({ 
                title: e.target.value 
              })}
              placeholder="Field Title (optional)"
              className="text-sm h-8"
              disabled={isLoading}
            />
            <p className="text-xs text-muted-foreground">
              Display name for users
            </p>
          </div>
        )}
        
        <div className="space-y-1">
          <Label className="text-xs">Description</Label>
          <Input
            value={field.description || ''}
            onChange={(e) => handleFieldUpdate({ 
              description: e.target.value 
            })}
            placeholder="Field description (optional)"
            className="text-sm h-8"
            disabled={isLoading}
          />
          <p className="text-xs text-muted-foreground">
            Help text for AI
          </p>
        </div>
      </div>

      {/* Field-Specific Configuration */}
      {children && (
        <div className="border-t pt-3">
          {children}
        </div>
      )}
    </div>
  )
}
